import type { PublishStatus } from '@/components/editor/PublishingSidebar';
import type { TranslatedValue } from '@/lib/forms/translatable';
import type { TaxonomyRef } from '@/routes/news/types';

/**
 * The rows `/admin/documents` and `/admin/document-categories` return.
 *
 * Hand-written for the reason `lib/api/resource.ts` sets out: Scramble types a
 * translatable field as `{[key: string]: unknown}` because it cannot see
 * through the per-locale map `AdminResource::translations()` builds.
 */

/** docs/03-DATA-MODEL.md §6's `scope` enum. */
export const DOCUMENT_SCOPES = [
  'university',
  'faculty',
  'vocational',
  'quality_assurance',
] as const;

/** Its `level` enum — the filter pills on the public library. */
export const DOCUMENT_LEVELS = ['all', 'bachelor', 'master', 'phd', 'vocational'] as const;

export type DocumentScope = (typeof DOCUMENT_SCOPES)[number];
export type DocumentLevel = (typeof DOCUMENT_LEVELS)[number];

export interface DocumentRow {
  id: number;

  title: TranslatedValue;
  description: TranslatedValue;

  category_id: number;
  category: TaxonomyRef | null;

  scope: DocumentScope;
  faculty_id: number | null;
  faculty: TaxonomyRef | null;
  level: DocumentLevel;

  /**
   * The file, as much of it as an editor needs. There is deliberately no
   * `file_path` — §6 says the raw storage path is never exposed, and the
   * download controller is the only way to the bytes.
   */
  file_name: string;
  file_type: string;
  file_size: number;
  file_hash_short: string;

  year: number | null;
  document_date: string | null;
  download_count: number;
  is_public: boolean;
  legacy_url: string | null;
  sort_order: number;

  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;

  translations_available: string[];
  translation_completeness: Record<string, number>;

  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: { id: number; name: string } | null;
}

/** The metadata step's payload — §6's all-locales shape, with no file in it. */
export interface DocumentInput {
  title: TranslatedValue;
  description: TranslatedValue;
  category_id: number;
  scope: DocumentScope;
  faculty_id: number | null;
  level: DocumentLevel;
  year: number | null;
  document_date: string | null;
  is_public: boolean;
  sort_order: number;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

/** What `POST /admin/documents/upload` answers with. */
export interface UploadResult {
  created: DocumentRow[];
  /** §5.4's "this file already exists as X". */
  duplicates: { file_name: string; existing: { id: number; title: string } }[];
}

/** §5.4's delete warning — one row per thing pointing at the document. */
export interface DocumentReference {
  type: 'announcement' | 'modal' | 'vacancy' | 'page' | 'menu_item';
  id: number;
  label: string;
}

export interface DocumentCategoryRow {
  id: number;
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  parent_id: number | null;
  parent: TaxonomyRef | null;
  sort_order: number;
  /** Null when the server did not count — never read as zero. */
  documents_count: number | null;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
}

export interface DocumentCategoryInput {
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  parent_id: number | null;
  sort_order: number;
}

/**
 * A size an editor can read.
 *
 * Georgian units, because §6.6 of the admin spec asks for a Georgian-first
 * dashboard and "1.5 MB" beside a Georgian label is the kind of half-translated
 * detail the current site is full of.
 */
export function formatFileSize(bytes: number, unit: (key: string) => string): string {
  if (bytes < 1024) {
    return `${bytes} ${unit('b')}`;
  }

  const kb = bytes / 1024;

  if (kb < 1024) {
    return `${Math.round(kb)} ${unit('kb')}`;
  }

  // One decimal place: the difference between 1.4 and 1.5 MB matters to
  // somebody on a phone; the difference between 1.47 and 1.48 does not.
  return `${(kb / 1024).toFixed(1)} ${unit('mb')}`;
}
